import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import type { Campaign } from '../types'
import { CampaignService } from '../services'
import { loadCampaignsFromDatabase } from '@/features/enrollment'
import { useAuth } from '@/features/auth'

interface CampaignContextType {
  campaigns: Campaign[]
  selectedCampaign: Campaign | null
  isLoading: boolean
  error: string | null
  selectCampaign: (campaign: Campaign | null) => void
  setSelectedCampaign: (campaign: Campaign | null) => void  // ✅ Added
  isCampaignSelected: boolean                                // ✅ Added
  refreshCampaigns: () => Promise<void>
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined)

export const CampaignProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)
  const [isLoading, setIsLoading] = useState(false) // ✅ Start as false
  const [error, setError] = useState<string | null>(null)
  const [hasInitialized, setHasInitialized] = useState(false) // ✅ Track initialization

  /**
   * Load campaigns from database and initialize configuration cache
   */
  const loadCampaigns = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      
      // Fetch campaigns from API
      const response = await CampaignService.getCampaigns({
        status: 'ACTIVE' // Only load active campaigns
      })
      
      if (response.data) {
        setCampaigns(response.data)
        
        // Load campaign configurations into memory cache
        await loadCampaignsFromDatabase(response.data)
        
      } else {
        throw new Error('Failed to load campaigns')
      }
    } catch (err: any) {
      console.error('❌ Error loading campaigns:', err)
      setError(err.message || 'Failed to load campaigns')
      setCampaigns([])
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Select a campaign
   */
  const selectCampaign = (campaign: Campaign | null) => {
    setSelectedCampaign(campaign)
    
    // Store in session storage for persistence
    if (campaign) {
      sessionStorage.setItem('selectedCampaignId', campaign.id)
    } else {
      sessionStorage.removeItem('selectedCampaignId')
    }
  }

  /**
   * Refresh campaigns (public method)
   */
  const refreshCampaigns = async () => {
    await loadCampaigns()
  }

  /**
   * Initialize: Load campaigns when user becomes authenticated
   */
  useEffect(() => {
    if (isAuthenticated && !hasInitialized) {
      loadCampaigns()
      setHasInitialized(true)
    } else if (!isAuthenticated && hasInitialized) {
      setCampaigns([])
      setSelectedCampaign(null)
      setHasInitialized(false)
    }
  }, [isAuthenticated, hasInitialized])

  /**
   * Handle authentication state changes
   */
  useEffect(() => {
    if (!isAuthenticated) {
      setCampaigns([])
      setSelectedCampaign(null)
      setError(null)
      setHasInitialized(false)
    }
  }, [isAuthenticated])

  /**
   * Restore selected campaign from session storage
   */
  useEffect(() => {
    if (campaigns.length > 0 && !selectedCampaign) {
      const savedCampaignId = sessionStorage.getItem('selectedCampaignId')
      
      if (savedCampaignId) {
        const campaign = campaigns.find(c => c.id === savedCampaignId)
        if (campaign) {
          setSelectedCampaign(campaign)
        }
      }
    }
  }, [campaigns])

  const value: CampaignContextType = {
    campaigns,
    selectedCampaign,
    isLoading,
    error,
    selectCampaign,
    setSelectedCampaign,              // ✅ Added
    isCampaignSelected: !!selectedCampaign,  // ✅ Added
    refreshCampaigns
  }

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  )
}

/**
 * Hook to use campaign context
 */
export const useCampaign = (): CampaignContextType => {
  const context = useContext(CampaignContext)
  
  if (!context) {
    throw new Error('useCampaign must be used within a CampaignProvider')
  }
  
  return context
}

export default CampaignContext